import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Plus, Trash2, ExternalLink, Eye, EyeOff, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
};

const getToken = () => { try { return localStorage.getItem('auth_token'); } catch { return null; } };

export default function BlogAdminPanel() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = getToken();
      const r = await fetch(`${API_URL}/api/blog/admin/posts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) { setErr(`Server returned ${r.status}`); return; }
      const data = await r.json();
      setPosts(data.posts || []);
    } catch (e) {
      setErr(e.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user || user.role !== 'admin') return null;

  const togglePublished = async (post) => {
    const token = getToken();
    await fetch(`${API_URL}/api/blog/posts/${encodeURIComponent(post.slug)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ published: !post.published }),
    });
    load();
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    const token = getToken();
    await fetch(`${API_URL}/api/blog/posts/${encodeURIComponent(post.slug)}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    load();
  };

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="blog-admin-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-cyan-400" />
          <h3 className="text-base font-bold text-white font-['Outfit']">Blog Posts</h3>
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500">// Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="blog-admin-refresh"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="blog-admin-new-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-[11px] font-bold tracking-wider uppercase rounded-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {showForm ? 'Close' : 'New Post'}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm mb-3">{err}</div>
      )}

      {showForm && <NewPostForm onCreated={() => { setShowForm(false); load(); }} />}

      <div className="overflow-x-auto rounded-sm border border-[#27272A]">
        <table className="w-full text-xs">
          <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Slug</th>
              <th className="text-left px-3 py-2">Published</th>
              <th className="text-right px-3 py-2">Views</th>
              <th className="text-right px-3 py-2">Read</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {posts.length === 0 && (
              <tr><td colSpan="6" className="text-center text-zinc-500 py-6">No posts yet. Click "New Post" to create one.</td></tr>
            )}
            {posts.map((p) => (
              <tr key={p.slug} className="hover:bg-[#171717] transition-colors" data-testid={`blog-row-${p.slug}`}>
                <td className="px-3 py-2.5 text-white font-medium max-w-[280px] truncate">{p.title}</td>
                <td className="px-3 py-2.5 text-[10px] text-zinc-500 font-['JetBrains_Mono']">{p.slug}</td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => togglePublished(p)}
                    data-testid={`blog-toggle-${p.slug}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider ${
                      p.published
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20'
                        : 'bg-zinc-700/30 border-zinc-700 text-zinc-400 hover:bg-zinc-700/50'
                    }`}
                  >
                    {p.published ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                    {p.published ? 'Live' : 'Draft'}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{p.views || 0}</td>
                <td className="px-3 py-2.5 text-right text-zinc-400 font-['JetBrains_Mono']">{p.read_minutes || '—'}m</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-cyan-400"
                      data-testid={`blog-view-${p.slug}`}
                      title="View post"
                    ><ExternalLink className="w-3.5 h-3.5" /></a>
                    <button
                      onClick={() => deletePost(p)}
                      className="text-zinc-400 hover:text-red-400"
                      data-testid={`blog-delete-${p.slug}`}
                      title="Delete post"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-zinc-500 mt-3 font-['JetBrains_Mono']">
        Paste BabyLoveGrowth output (markdown) into the body field. Posts publish to /blog/&lt;slug&gt;. Click "Live/Draft" to toggle visibility.
      </p>
    </div>
  );
}

function NewPostForm({ onCreated }) {
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const token = getToken();
      const r = await fetch(`${API_URL}/api/blog/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          title: title.trim(),
          excerpt: excerpt.trim(),
          body: body.trim(),
          tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
          published: true,
        }),
      });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        throw new Error(errData.detail || `Server returned ${r.status}`);
      }
      onCreated();
      setTitle(''); setExcerpt(''); setBody(''); setTags('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4 mb-4 space-y-3" data-testid="new-post-form">
      <input
        type="text"
        required
        placeholder="Title (required)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="new-post-title"
        className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF5E1A]/60"
      />
      <input
        type="text"
        required
        placeholder="Excerpt — 1–2 sentence summary (required)"
        value={excerpt}
        onChange={(e) => setExcerpt(e.target.value)}
        data-testid="new-post-excerpt"
        className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF5E1A]/60"
      />
      <input
        type="text"
        placeholder="Tags, comma-separated (e.g. BMW, MSV80, tuning)"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        data-testid="new-post-tags"
        className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF5E1A]/60"
      />
      <textarea
        required
        rows="14"
        placeholder="Body — paste markdown here (## headings, **bold**, [links](/green-light), tables, lists all supported)"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="new-post-body"
        className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 font-['JetBrains_Mono'] focus:outline-none focus:border-[#FF5E1A]/60"
      />
      {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm">{err}</div>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="new-post-submit"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-50 text-white text-xs font-bold tracking-wider uppercase rounded-sm transition-colors"
        >
          {submitting ? 'Publishing…' : 'Publish'}
        </button>
        <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">
          Slug auto-generated from title. Posts go live immediately.
        </span>
      </div>
    </form>
  );
}
